import {
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { Folder } from "@models";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "src/components/Snackbar";
import {
  usePatchDocumentMutation,
  usePatchFolderMutation,
} from "src/hooks/mutations";

import type { DocumentsPageItem } from "../Documents.utils";
import type { DocumentsTableMoveTarget } from "../DocumentsTable.types";
import {
  buildDocumentsTableItemsById,
  buildDocumentsTableMovePlan,
  getDocumentsTableMoveSubjectIds,
  isDocumentsTableItemId,
  resolveDocumentsTableMoveTarget,
  type DocumentsTableItemId,
} from "./DocumentsTable.dnd";

type PatchDocumentMoveArgs = {
  data: {
    folderId: string | null;
  };
  deviceId: string;
  docId: string;
  orgId: string;
  queryDeviceIds: string[];
};

type PatchFolderMoveArgs = {
  data: {
    parentFolderId: string | null;
  };
  folderId: string;
  orgId: string;
};

type MutationLike<TArgs> = {
  mutate: (args: TArgs) => void;
  mutateAsync?: (args: TArgs) => Promise<unknown>;
};

const runMutation = <TArgs>(mutation: MutationLike<TArgs>, args: TArgs) => {
  if (mutation.mutateAsync) {
    return mutation.mutateAsync(args);
  }

  mutation.mutate(args);

  return Promise.resolve();
};

const isShiftKeyHeld = (event: Event) =>
  (event as Event & { shiftKey?: boolean }).shiftKey === true;

export const useDocumentsTableMoveController = ({
  currentDeviceId,
  documentQueryDeviceIds,
  folders,
  fullList,
  orgId,
  selectedItemIds,
}: {
  currentDeviceId: string | null;
  documentQueryDeviceIds: string[];
  folders: Folder[];
  fullList: DocumentsPageItem[];
  orgId: string;
  selectedItemIds: DocumentsTableItemId[];
}) => {
  const { showSnackbar } = useSnackbar();
  const patchDocumentMutation = usePatchDocumentMutation();
  const patchFolderMutation = usePatchFolderMutation();
  const [activeDragItemId, setActiveDragItemId] =
    useState<DocumentsTableItemId | null>(null);
  const [activeDragSubjectItemIds, setActiveDragSubjectItemIds] = useState<
    DocumentsTableItemId[]
  >([]);
  const [activeDragRequiresShiftKey, setActiveDragRequiresShiftKey] =
    useState(false);
  const activeDragSubjectItemIdsRef = useRef<DocumentsTableItemId[]>([]);
  const isActiveDragCancelledRef = useRef(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    })
  );
  const foldersById = useMemo(
    () => new Map(folders.map((folder) => [folder.id, folder])),
    [folders]
  );
  const itemsById = useMemo(
    () => buildDocumentsTableItemsById(fullList),
    [fullList]
  );
  const isMoving =
    patchDocumentMutation.isPending || patchFolderMutation.isPending;

  const resetDragState = useCallback(() => {
    activeDragSubjectItemIdsRef.current = [];
    setActiveDragItemId(null);
    setActiveDragRequiresShiftKey(false);
    setActiveDragSubjectItemIds([]);
  }, []);

  const cancelActiveDrag = useCallback(() => {
    isActiveDragCancelledRef.current = true;
    resetDragState();
  }, [resetDragState]);

  useEffect(() => {
    if (!activeDragRequiresShiftKey) {
      return;
    }

    const handleKeyUp = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Shift") {
        cancelActiveDrag();
      }
    };

    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", cancelActiveDrag);

    return () => {
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", cancelActiveDrag);
    };
  }, [activeDragRequiresShiftKey, cancelActiveDrag]);

  const moveItemIdsToTarget = useCallback(
    async ({
      itemIds,
      target,
    }: {
      itemIds: DocumentsTableItemId[];
      target: DocumentsTableMoveTarget;
    }) => {
      const plan = buildDocumentsTableMovePlan({
        foldersById,
        itemIds,
        itemsById,
        target,
      });

      if (!plan) {
        return;
      }

      try {
        await Promise.all([
          ...plan.folderMoves.map((folderId) =>
            runMutation<PatchFolderMoveArgs>(patchFolderMutation, {
              orgId,
              folderId,
              data: {
                parentFolderId:
                  target.type === "folder" ? target.folderId : null,
              },
            })
          ),
          ...plan.documentMoves.map((document) =>
            runMutation<PatchDocumentMoveArgs>(patchDocumentMutation, {
              orgId,
              docId: document.id,
              deviceId: document.deviceId ?? currentDeviceId ?? "",
              queryDeviceIds: document.deviceId
                ? [document.deviceId]
                : documentQueryDeviceIds,
              data: {
                folderId: target.type === "folder" ? target.folderId : null,
              },
            })
          ),
        ]);
      } catch {
        showSnackbar("Failed to move item. Please try again.");
      }
    },
    [
      currentDeviceId,
      documentQueryDeviceIds,
      foldersById,
      itemsById,
      orgId,
      patchDocumentMutation,
      patchFolderMutation,
      showSnackbar,
    ]
  );

  const handleDragStart = useCallback(
    ({ active, activatorEvent }: DragStartEvent) => {
      const activeId = `${active.id}`;

      if (!isDocumentsTableItemId(activeId)) {
        resetDragState();
        return;
      }

      const subjectItemIds = getDocumentsTableMoveSubjectIds({
        draggedItemId: activeId,
        selectedItemIds,
      });
      activeDragSubjectItemIdsRef.current = subjectItemIds;
      isActiveDragCancelledRef.current = false;
      setActiveDragItemId(activeId);
      setActiveDragRequiresShiftKey(
        isShiftKeyHeld(activatorEvent) &&
          selectedItemIds.length > 1 &&
          selectedItemIds.includes(activeId)
      );
      setActiveDragSubjectItemIds(subjectItemIds);
    },
    [resetDragState, selectedItemIds]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      const itemIds = activeDragSubjectItemIdsRef.current;
      const wasCancelled = isActiveDragCancelledRef.current;
      resetDragState();
      isActiveDragCancelledRef.current = false;

      if (wasCancelled || itemIds.length === 0) {
        return;
      }

      const activeId = `${active.id}`;

      if (!isDocumentsTableItemId(activeId)) {
        return;
      }

      const target = resolveDocumentsTableMoveTarget({
        foldersById,
        itemsById,
        targetId: over ? `${over.id}` : null,
      });

      if (!target) {
        return;
      }

      void moveItemIdsToTarget({
        itemIds,
        target,
      });
    },
    [foldersById, itemsById, moveItemIdsToTarget, resetDragState]
  );

  const handleDragCancel = useCallback(() => {
    resetDragState();
    isActiveDragCancelledRef.current = false;
  }, [resetDragState]);

  const cancelDrop = useCallback(() => isActiveDragCancelledRef.current, []);

  return {
    activeDragItemId,
    activeDragSubjectItemIds,
    cancelDrop,
    foldersById,
    handleDragCancel,
    handleDragEnd,
    handleDragStart,
    isMoving,
    itemsById,
    sensors,
  };
};

export type DocumentsTableMoveController = ReturnType<
  typeof useDocumentsTableMoveController
>;
